import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, InputNumber, Select, Button, Typography, Alert, Divider, Row, Col, Radio, Tabs } from 'antd';
import { calculateSalaryDetails } from '../salaryCalculations';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

// Helper function to format currency with commas and 2 decimal places
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const TargetNetSalaryCalculator = ({ inputValues, selectedTaxYear }) => {
  // Target value states
  const [targetNetSalaryAnnual, setTargetNetSalaryAnnual] = useState('');
  const [targetNetSalaryMonthly, setTargetNetSalaryMonthly] = useState('');
  const [targetTaxableIncome, setTargetTaxableIncome] = useState('');
  
  // Pension input states
  const [pensionInputMode, setPensionInputMode] = useState('percentage'); // 'percentage' or 'amount'
  const [pensionPercentage, setPensionPercentage] = useState(0);
  const [pensionAmount, setPensionAmount] = useState(0);
  
  // Other states
  const [activeTab, setActiveTab] = useState('1'); // Default to annual net salary tab
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [baselineData, setBaselineData] = useState(null);
  
  // Helper to convert pension amount to percentage based on base salary
  const calculatePensionPercentage = useCallback((amount) => {
    if (!inputValues?.baseSalary || inputValues.baseSalary === 0) return 0;
    return (amount / inputValues.baseSalary) * 100;
  }, [inputValues?.baseSalary]);
  
  // Helper to convert pension percentage to amount based on base salary
  const calculatePensionAmount = useCallback((percentage) => {
    if (!inputValues?.baseSalary) return 0;
    // Round to 2 decimal places to avoid floating point issues
    return Math.round((percentage / 100) * inputValues.baseSalary * 100) / 100;
  }, [inputValues?.baseSalary]);
  
// Update pension percentage when amount changes
  useEffect(() => {
    if (pensionInputMode === 'amount') {
      const calculatedPct = calculatePensionPercentage(pensionAmount);
      // Round to 1 decimal place for percentage
      setPensionPercentage(Math.round(calculatedPct * 10) / 10);
    }
  }, [pensionAmount, pensionInputMode, calculatePensionPercentage]);
  
  // Update pension amount when percentage changes
  useEffect(() => {
    if (pensionInputMode === 'percentage') {
      const calculatedAmount = calculatePensionAmount(pensionPercentage);
      // Round to 2 decimal places for currency
      setPensionAmount(Math.round(calculatedAmount * 100) / 100);
    }
  }, [pensionPercentage, pensionInputMode, calculatePensionAmount]);

  // Calculate baseline data (with 0 RSU)
  const calculateBaseline = useCallback(() => {
    if (!inputValues || !inputValues.baseSalary) return;
    
    try {
      // Create a deep copy of input values
      const baseInputs = JSON.parse(JSON.stringify(inputValues));
      
      // Completely remove all RSU data
      baseInputs.rsus = 0;
      baseInputs.rsuVestEvents = [];
      baseInputs.capitalGains = 0;
      
      // Set the pension percentage
      const effectivePensionPct = pensionInputMode === 'percentage' ? 
        pensionPercentage : calculatePensionPercentage(pensionAmount);
      baseInputs.employeePensionContributionPct = effectivePensionPct;
      
      // Run the calculation
      const results = calculateSalaryDetails(baseInputs, selectedTaxYear);
      
      // Find the result for our pension percentage
      const result = results.find(item => 
        Math.abs(parseFloat(item.employee_pension_contribution_pct) - effectivePensionPct) < 0.1
      );
      
      if (result) {
        setBaselineData({
          netSalary: parseFloat(result.net_salary),
          netSalaryExclRsu: parseFloat(result.net_salary_excl_rsu),
          taxableIncome: parseFloat(result.taxable_income_less_pension),
          employeePensionAmount: parseFloat(result.employee_pension_amount)
        });
      }
    } catch (err) {
      console.error('Error calculating baseline:', err);
      setError('Failed to calculate baseline data');
    }
  }, [inputValues, pensionPercentage, pensionAmount, pensionInputMode, selectedTaxYear, calculatePensionPercentage]);

  // Calculate when inputs change
  useEffect(() => {
    calculateBaseline();
  }, [calculateBaseline]);

  // Handle annual net salary target input
  const handleNetSalaryAnnualChange = (value) => {
    setTargetNetSalaryAnnual(value);
    // Update monthly value automatically
    if (value) {
      setTargetNetSalaryMonthly((value / 12).toFixed(2));
    } else {
      setTargetNetSalaryMonthly('');
    }
  };
  
  // Handle monthly net salary target input
  const handleNetSalaryMonthlyChange = (value) => {
    setTargetNetSalaryMonthly(value);
    // Update annual value automatically
    if (value) {
      setTargetNetSalaryAnnual((value * 12).toFixed(2));
    } else {
      setTargetNetSalaryAnnual('');
    }
  };
  
  // Handle calculation button click
  const handleCalculate = () => {
    // Determine which target value to use based on the active tab
    let targetValue;
    let targetType;
    
    if (activeTab === '1') {
      // Annual net salary target
      targetValue = parseFloat(targetNetSalaryAnnual);
      targetType = 'netSalary';
      
      if (!targetValue) {
        setError('Please enter a target annual net salary');
        return;
      }
    } else if (activeTab === '2') {
      // Monthly net salary target
      targetValue = parseFloat(targetNetSalaryMonthly) * 12; // Convert to annual
      targetType = 'netSalary';
      
      if (!targetValue) {
        setError('Please enter a target monthly net salary');
        return;
      }
    } else if (activeTab === '3') {
      // Taxable income target
      targetValue = parseFloat(targetTaxableIncome);
      targetType = 'taxableIncome';
      
      if (!targetValue) {
        setError('Please enter a target taxable income');
        return;
      }
    }
    
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Create a trial-and-error search for the RSU amount
      let currentRSU = 0;
      let step = 5000; // Start with £5,000 increments
      let bestRSU = 0;
      let bestNetSalary = 0;
      let bestTaxableIncome = 0;
      let direction = 1; // 1 for increasing, -1 for decreasing
      
      // Get the effective pension percentage to use
      const effectivePensionPct = pensionInputMode === 'percentage' ? 
        pensionPercentage : calculatePensionPercentage(pensionAmount);
      
      for (let i = 0; i < 50; i++) { // Limit iterations to avoid infinite loops
        // Prepare inputs for this calculation
        const testInputs = JSON.parse(JSON.stringify(inputValues));
        
        // Set RSUs explicitly and clear rsuVestEvents
        testInputs.rsus = currentRSU;
        testInputs.rsuVestEvents = [];
        testInputs.capitalGains = 0;
        testInputs.employeePensionContributionPct = effectivePensionPct;
        
        // Run calculation
        const results = calculateSalaryDetails(testInputs, selectedTaxYear);
        
        // Find the result for our pension percentage
        const result = results.find(item => 
          Math.abs(parseFloat(item.employee_pension_contribution_pct) - effectivePensionPct) < 0.1
        );
        
        if (!result) {
          console.error('No matching result found for pension percentage:', effectivePensionPct);
          continue;
        }
        
        // Get values from result based on target type
        const currentNetSalary = parseFloat(result.net_salary);
        const currentTaxableIncome = parseFloat(result.taxable_income_less_pension);
        
        // Save for logging
        const currentValue = targetType === 'netSalary' ? currentNetSalary : currentTaxableIncome;
        console.log(`Iteration ${i}: RSU ${currentRSU}, ${targetType}=${currentValue}, Target ${targetValue}`);
        
        // Check if this is the best result so far - use the appropriate value for comparison
        const diff = targetType === 'netSalary' 
          ? Math.abs(currentNetSalary - targetValue)
          : Math.abs(currentTaxableIncome - targetValue);
        
        const bestDiff = targetType === 'netSalary'
          ? Math.abs(bestNetSalary - targetValue)
          : Math.abs(bestTaxableIncome - targetValue);
        
        if (i === 0 || diff < bestDiff) {
          bestRSU = currentRSU;
          bestNetSalary = currentNetSalary;
          bestTaxableIncome = currentTaxableIncome;
          
          // If we're close enough, we can stop
          if (diff < 10) {
            break;
          }
        }
        
        // Adjust step size
        if (diff < 5000) {
          step = 500;
        } else if (diff < 20000) {
          step = 2000;
        } else {
          step = 5000;
        }
        
        // Determine direction based on target type
        const shouldGoUp = targetType === 'netSalary'
          ? currentNetSalary < targetValue
          : currentTaxableIncome < targetValue;
        
        const shouldGoDown = targetType === 'netSalary'
          ? currentNetSalary > targetValue
          : currentTaxableIncome > targetValue;
        
        // Change direction if we overshoot
        if ((direction > 0 && shouldGoDown) || (direction < 0 && shouldGoUp)) {
          direction = -direction;
          step = Math.max(step / 2, 100); // Reduce step size but not below £100
        }
        
        // Update the RSU amount for the next iteration
        currentRSU += direction * step;
        
        // Ensure RSU doesn't go negative
        if (currentRSU < 0) {
          currentRSU = 0;
          direction = 1;
        }
      }
      
      // Get the final detailed calculation for the best RSU amount
      const finalInputs = JSON.parse(JSON.stringify(inputValues));
      finalInputs.rsus = bestRSU;
      finalInputs.rsuVestEvents = [];
      finalInputs.capitalGains = 0;
      finalInputs.employeePensionContributionPct = effectivePensionPct;
      
      const finalResults = calculateSalaryDetails(finalInputs, selectedTaxYear);
      const finalResult = finalResults.find(item => 
        Math.abs(parseFloat(item.employee_pension_contribution_pct) - effectivePensionPct) < 0.1
      );
      
      if (finalResult) {
        setResult({
          requiredRSUs: bestRSU,
          achievedNetSalary: parseFloat(finalResult.net_salary),
          achievedNetSalaryMonthly: parseFloat(finalResult.net_salary) / 12,
          achievedTaxableIncome: parseFloat(finalResult.taxable_income_less_pension),
          rsuTax: parseFloat(finalResult.rsu_tax || 0),
          rsuNIC: parseFloat(finalResult.rsu_nic_amount || 0),
          details: finalResult,
          targetType: targetType
        });
      } else {
        setError('Failed to calculate final result');
      }
      
    } catch (err) {
      console.error('Calculation error:', err);
      setError('Error during calculation: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const calculateEmployerPensionAmount = useCallback(() => {
    if (!inputValues?.baseSalary || !inputValues?.employerMaxPensionContributionPct) return 0;
    
    // Employer contribution is the minimum of the employee % and employer max %
    const employerContributionPct = Math.min(
      pensionInputMode === 'percentage' ? 
        pensionPercentage : 
        calculatePensionPercentage(pensionAmount),
      inputValues.employerMaxPensionContributionPct
    );
    
    return Math.round((employerContributionPct / 100) * inputValues.baseSalary * 100) / 100;
  }, [inputValues?.baseSalary, inputValues?.employerMaxPensionContributionPct, pensionPercentage, pensionAmount, pensionInputMode, calculatePensionPercentage]);

  // Generate pension percentage options - 0% to 50% in 0.5% increments
  const pensionOptions = Array.from({ length: 101 }, (_, i) => i * 0.5).map(value => (
    <Option key={value} value={value}>{value.toFixed(1)}%</Option>
  ));

  return (
    <Card 
      title={<Title level={4}>Target Salary Calculator</Title>}
      style={{ marginTop: 20, marginBottom: 20 }}
    >
      <Text>
        Calculate how many RSUs you need to vest to achieve your target salary goals.
        This calculator uses your provided salary and deduction details.
      </Text>
      
      {baselineData && (
        <Alert
          message="Current Financial Information"
          description={
            <div>
              <div>
                <Text>Your annual net salary without any RSUs: </Text>
                <Text strong>{formatCurrency(baselineData.netSalary)}</Text>
              </div>
              <div>
                <Text>Your monthly net salary without any RSUs: </Text>
                <Text strong>{formatCurrency(baselineData.netSalary / 12)}</Text>
              </div>
              <div>
                <Text>Your taxable income without any RSUs: </Text>
                <Text strong>{formatCurrency(baselineData.taxableIncome)}</Text>
              </div>
            </div>
          }
          type="info"
          showIcon
          style={{ marginTop: 16, marginBottom: 16 }}
        />
      )}
      
      <Divider />
      
      <Form layout="vertical">
        {/* Pension Input Section */}
        <Row gutter={16}>
          <Col xs={24} sm={24} md={24}>
            <Form.Item
              label={
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Employee Pension Contribution</Text>
                </div>
              }
            >
              <Radio.Group 
                value={pensionInputMode} 
                onChange={(e) => setPensionInputMode(e.target.value)}
                style={{ marginBottom: 16 }}
              >
                <Radio.Button value="percentage">Percentage</Radio.Button>
                <Radio.Button value="amount">Amount (£)</Radio.Button>
              </Radio.Group>
              
              {pensionInputMode === 'percentage' ? (
                <Select
                  value={pensionPercentage}
                  onChange={setPensionPercentage}
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="children"
                >
                  {pensionOptions}
                </Select>
              ) : (
                <InputNumber
                  style={{ width: '100%' }}
                  value={pensionAmount}
                  onChange={setPensionAmount}
                  formatter={(value) => `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/£\s?|(,*)/g, '')}
                  min={0}
                  step={100}
                  placeholder="Enter pension contribution amount"
                />
              )}
              
              {pensionInputMode === 'percentage' && inputValues?.baseSalary && (
                <div style={{ marginTop: 8 }}>
                    <Text type="secondary">
                    Equivalent to {formatCurrency(Math.round(pensionAmount * 100) / 100)} per year
                    </Text>
                </div>
                )}
              
              {pensionInputMode === 'amount' && inputValues?.baseSalary && (
                <div style={{ marginTop: 8 }}>
                    <Text type="secondary">
                    Equivalent to {(Math.round(pensionPercentage * 100) / 100).toFixed(2)}% of your salary
                    </Text>
                </div>
                )}

                {inputValues?.baseSalary && (
                <div style={{ marginTop: 8 }}>
                    <Text type="secondary">
                    Total pension contribution (including employer): {formatCurrency(pensionAmount + calculateEmployerPensionAmount())}
                    </Text>
                </div>
                )}
            </Form.Item>
          </Col>
        </Row>
        
        {/* Target Input Tabs */}
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Annual Net Salary" key="1">
            <Form.Item label="Target Annual Net Salary">
              <InputNumber
                style={{ width: '100%' }}
                value={targetNetSalaryAnnual}
                onChange={handleNetSalaryAnnualChange}
                formatter={(value) => `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/£\s?|(,*)/g, '')}
                min={0}
                step={1000}
                placeholder="Enter your target annual net salary"
              />
            </Form.Item>
          </TabPane>
          
          <TabPane tab="Monthly Net Salary" key="2">
            <Form.Item label="Target Monthly Net Salary">
              <InputNumber
                style={{ width: '100%' }}
                value={targetNetSalaryMonthly}
                onChange={handleNetSalaryMonthlyChange}
                formatter={(value) => `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/£\s?|(,*)/g, '')}
                min={0}
                step={100}
                placeholder="Enter your target monthly net salary"
              />
            </Form.Item>
          </TabPane>
          
          <TabPane tab="Taxable Income" key="3">
            <Form.Item label="Target Annual Taxable Income">
              <InputNumber
                style={{ width: '100%' }}
                value={targetTaxableIncome}
                onChange={setTargetTaxableIncome}
                formatter={(value) => `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/£\s?|(,*)/g, '')}
                min={0}
                step={1000}
                placeholder="Enter your target annual taxable income"
              />
            </Form.Item>
          </TabPane>
        </Tabs>
        
        <Form.Item>
          <Button 
            type="primary" 
            onClick={handleCalculate}
            loading={loading}
            block
          >
            Calculate Required RSUs
          </Button>
        </Form.Item>
      </Form>
      
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
      
      {result && (
        <Card 
          type="inner" 
          title="Calculation Result" 
          style={{ marginTop: 16 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text strong>Required RSU Amount:</Text>
            <Text>{formatCurrency(result.requiredRSUs)}</Text>
          </div>
          
          <Divider style={{ margin: '12px 0' }}/>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text strong>Annual Net Salary:</Text>
            <Text>{formatCurrency(result.achievedNetSalary)}</Text>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text strong>Monthly Net Salary:</Text>
            <Text>{formatCurrency(result.achievedNetSalaryMonthly)}</Text>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text strong>Annual Taxable Income:</Text>
            <Text>{formatCurrency(result.achievedTaxableIncome)}</Text>
          </div>
          
          <Divider style={{ margin: '12px 0' }}/>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text strong>Tax on RSUs:</Text>
            <Text>{formatCurrency(result.rsuTax)}</Text>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text strong>NIC on RSUs:</Text>
            <Text>{formatCurrency(result.rsuNIC)}</Text>
          </div>
          
          <Alert
            message="Target Result"
            description={
              result.targetType === 'netSalary' ? (
                Math.abs(result.achievedNetSalary - (activeTab === '2' ? parseFloat(targetNetSalaryMonthly) * 12 : parseFloat(targetNetSalaryAnnual))) < 100
                  ? `With ${formatCurrency(result.requiredRSUs)} of RSUs vested, your annual net salary would be approximately ${formatCurrency(result.achievedNetSalary)} (${formatCurrency(result.achievedNetSalaryMonthly)} monthly).`
                  : `With ${formatCurrency(result.requiredRSUs)} of RSUs vested, your annual net salary would be approximately ${formatCurrency(result.achievedNetSalary)} (${formatCurrency(result.achievedNetSalaryMonthly)} monthly), which is the closest we could get to your target.`
              ) : (
                Math.abs(result.achievedTaxableIncome - parseFloat(targetTaxableIncome)) < 100
                  ? `With ${formatCurrency(result.requiredRSUs)} of RSUs vested, your annual taxable income would be approximately ${formatCurrency(result.achievedTaxableIncome)}.`
                  : `With ${formatCurrency(result.requiredRSUs)} of RSUs vested, your annual taxable income would be approximately ${formatCurrency(result.achievedTaxableIncome)}, which is the closest we could get to your target of ${formatCurrency(parseFloat(targetTaxableIncome))}.`
              )
            }
            type="success"
            showIcon
          />
          
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">
              Note: This is an estimate based on your inputs. Actual results may vary based on the exact timing of RSU vests and other factors.
            </Text>
          </div>
        </Card>
      )}
    </Card>
  );
};

export default TargetNetSalaryCalculator;